import { createAction } from '@reduxjs/toolkit';
import { Moment } from 'moment';
import { AutomationSource } from 'api/automation-builder/types';
import { Automation } from 'api/automations';
import { Team, User } from 'api/models';
import { Filter } from 'components/molecules/filters/types';
import { BulkActionPayloadMap } from 'components/organisms/resource-management/types';
import { FilterableField } from 'components/organisms/tasks/fields';
import { PositionData } from 'components/organisms/templates/projects/commons/rearrange/types';
import { TemplateState } from '../api';
import {
  InsertDirection,
  ProjectTemplateApi,
  ProjectTemplateProjectPhase,
  ProjectTemplateSpace,
  ProjectTemplateTask,
} from './types';

interface BaseActionPayload {
  handleFailure?(): void;
  handleAlways?(): void;
}

export const fetchProjectTemplate = createAction<{
  templateId: number;
  dynamicConditionFields: FilterableField[];
}>('@@TEMPLATES/PROJECT/fetchProjectTemplate');

export interface SaveProjectTemplatePayload extends BaseActionPayload {
  type: TemplateState;
  handleSuccess?(template: ProjectTemplateApi): void;
  saveToIdb?: boolean;
}
export const saveProjectTemplate = createAction<SaveProjectTemplatePayload>(
  '@@TEMPLATES/PROJECTS/saveProjectTemplate',
);

// ---- PHASE ---- //
export interface CreatePhaseInProjectTemplatePayload extends BaseActionPayload {
  projectPhase: Partial<ProjectTemplateProjectPhase>;
  insertAt?: {
    id: number;
    direction: InsertDirection.Above | InsertDirection.Below;
  };
  handleSuccess?(
    projectPhase: AtLeast<ProjectTemplateProjectPhase, 'projectPhaseId'>,
  ): void;
}
export const createPhaseInProjectTemplate =
  createAction<CreatePhaseInProjectTemplatePayload>(
    '@@TEMPLATES/PROJECT/createPhaseInProjectTemplate',
  );

export interface UpdatePhaseInProjectTemplatePayload extends BaseActionPayload {
  projectPhaseId: number;
  payload: Partial<ProjectTemplateProjectPhase>;
  handleSuccess?(): void;
}
export const updatePhaseInProjectTemplate =
  createAction<UpdatePhaseInProjectTemplatePayload>(
    '@@TEMPLATES/PROJECT/updatePhaseInProjectTemplate',
  );

export interface DeletePhaseInProjectTemplate extends BaseActionPayload {
  projectPhaseId: number;
  handleSuccess?(): void;
}
export const deletePhaseInProjectTemplate =
  createAction<DeletePhaseInProjectTemplate>(
    '@@TEMPLATES/PROJECT/deletePhaseInProjectTemplate',
  );
export interface AddPhaseAboveInProjectTemplatePayload
  extends BaseActionPayload {
  projectPhaseId: number;
}

export const addPhaseAboveInProjectTemplate =
  createAction<AddPhaseAboveInProjectTemplatePayload>(
    '@@TEMPLATES/PROJECT/addPhaseAboveInProjectTemplate',
  );

export interface ShiftStartDayOfProjectPhaseInProjectTemplatePayload
  extends BaseActionPayload {
  projectPhaseId: number;
  shiftBy: number;
}

export const shiftStartDayOfProjectPhaseInProjectTemplate =
  createAction<ShiftStartDayOfProjectPhaseInProjectTemplatePayload>(
    '@@TEMPLATES/PROJECT/shiftStartDayProjectPhaseInProjectTemplate',
  );

// ---- TASK ---- //
export interface CreateTaskInProjectTemplate extends BaseActionPayload {
  projectPhaseId: number;
  payload?: {
    taskId: number;
    taskName?: string;
  };
}
export const createTaskInProjectTemplate =
  createAction<CreateTaskInProjectTemplate>(
    '@@TEMPLATES/PROJECT/createTaskInProjectTemplate',
  );

export interface CreateTaskBeforeInProjectTemplate {
  taskId: number;
  taskName?: string;
  projectPhaseId: number;
}

export const createTaskBeforeInProjectTemplate =
  createAction<CreateTaskBeforeInProjectTemplate>(
    '@@TEMPLATES/PROJECT/createTaskBeforeInProjectTemplate',
  );

export interface UpdateTaskInProjectTemplate extends BaseActionPayload {
  taskId: number;
  payload: Partial<ProjectTemplateTask>;
}
export const updateTaskInProjectTemplate =
  createAction<UpdateTaskInProjectTemplate>(
    '@@TEMPLATES/PROJECT/updateTaskInProjectTemplate',
  );

export const rearrangeTasksInProjectTemplate = createAction<PositionData[]>(
  '@@TEMPLATES/PROJECT/rearrangeTaskInProjectTemplate',
);
export interface TaskIdPayload extends BaseActionPayload {
  taskId: number;
  payload?: { taskId: number; taskName?: string };
}

export const deleteTaskInProjectTemplate = createAction<TaskIdPayload>(
  '@@TEMPLATES/PROJECT/deleteTaskInProjectTemplate',
);
export const addTaskBelowProjectTemplate = createAction<TaskIdPayload>(
  '@@TEMPLATES/PROJECT/addTaskBelowProjectTemplate',
);
export const addTaskAboveProjectTemplate = createAction<TaskIdPayload>(
  '@@TEMPLATES/PROJECT/addTaskAboveProjectTemplate',
);

export interface AddTaskDependencyInProjectTemplatePayload
  extends BaseActionPayload {
  taskId: number;
  dependentTaskId: number;
}
export const addTaskDependencyInProjectTemplate =
  createAction<AddTaskDependencyInProjectTemplatePayload>(
    '@@TEMPLATES/PROJECT/addTaskDependencyInProjectTemplate',
  );

export interface AddPhaseDependencyInProjectTemplatePayload
  extends BaseActionPayload {
  phaseId: number;
  dependentPhaseId: number;
}
export const addPhaseDependencyInProjectTemplate =
  createAction<AddPhaseDependencyInProjectTemplatePayload>(
    '@@TEMPLATES/PROJECT/addPhaseDependencyInProjectTemplate',
  );

// ---- SUB TASK ---- //
export interface CreateSubTaskInProjectTemplate extends BaseActionPayload {
  parentTaskId: number;
  payload?: { taskId: number; taskName?: '' };
}
export const createSubTaskInProjectTemplate =
  createAction<CreateSubTaskInProjectTemplate>(
    '@@TEMPLATES/PROJECT/createSubTaskInProjectTemplate',
  );

// ---- SPACES ---- //
export interface CreateSpaceInProjectTemplatePayload extends BaseActionPayload {
  space: Partial<Pick<ProjectTemplateSpace, 'private'>>;
}
export const createSpaceInProjectTemplate =
  createAction<CreateSpaceInProjectTemplatePayload>(
    '@@TEMPLATES/PROJECT/createSpaceInProjectTemplate',
  );

export interface MinSpaceIdInProjectTemplatePayload extends BaseActionPayload {
  space: AtLeast<ProjectTemplateSpace, 'spaceId'>;
}
export const updateSpaceInProjectTemplate =
  createAction<MinSpaceIdInProjectTemplatePayload>(
    '@@TEMPLATES/PROJECT/updateSpaceInProjectTemplate',
  );

export const deleteSpaceInProjectTemplate =
  createAction<MinSpaceIdInProjectTemplatePayload>(
    '@@TEMPLATES/PROJECT/deleteSpaceInProjectTemplate',
  );

export interface SetupProjectTemplateSpace extends BaseActionPayload {
  companyName: string;
}
export const setupSpaceInProjectTemplateAction =
  createAction<SetupProjectTemplateSpace>(
    '@@TEMPLATES/PROJECT/setupSpaceInProjectTemplateAction',
  );

// ---- SPACE TAB ---- //
export interface DeleteSpaceTabProjectTemplatePayload
  extends BaseActionPayload {
  spaceTabId: number;
  spaceId?: number;
}
export const deleteSpaceTabInProjectTemplateAction =
  createAction<DeleteSpaceTabProjectTemplatePayload>(
    '@@TEMPLATES/PROJECT/deleteSpaceTabInProjectTemplate',
  );

// ---- JIRA ---- //
export interface UpdateJiraMappingsInTemplateAction extends BaseActionPayload {
  taskId: number;
  data: Record<string, any> | undefined;
}
export const updateJiraMappingsInTemplateAction =
  createAction<UpdateJiraMappingsInTemplateAction>(
    '@@TEMPLATES/PROJECT/JiraMapping',
  );

// ---- DYNAMIC TEMPLATES ---- //

export interface UpdateDynamicTemplateConditionsActionPayload
  extends BaseActionPayload {
  entityId: number;
  conditions: Filter;
}
export const updateDynamicTemplateConditionsAction =
  createAction<UpdateDynamicTemplateConditionsActionPayload>(
    '@@TEMPLATES/PROJECT/dynamicConditions',
  );

export interface GetDynamicTemplatePreviewActionPayload
  extends BaseActionPayload {
  fields: any;
  onSuccess?: (templatePreview) => void;
  onFinally?: () => void;
  compressTimeline: boolean;
}
export const getDynamicTemplatePreviewAction =
  createAction<GetDynamicTemplatePreviewActionPayload>(
    '@@TEMPLATES/PROJECT/dynamic/preview',
  );
export interface AllocationBulkUpdatePayload extends BaseActionPayload {
  actionsMap: BulkActionPayloadMap;
  relativeDate: Moment;
  isHoursPerWeek?: boolean;
}
export const allocationsBulkAction = createAction<AllocationBulkUpdatePayload>(
  '@@TEMPLATES/PROJECT/allocation/bulkAction',
);

export interface UpdateAllocatedMembersPayload extends BaseActionPayload {
  users: User[];
  teams: Team[];
}
export const updateAllocatedMembersAction =
  createAction<UpdateAllocatedMembersPayload>(
    '@@TEMPLATES/PROJECT/allocation/updateMembers',
  );

// <--- Automations --->
export interface UpdateAllAutomationsPayload {
  automations: Automation[];
}

export interface UpdateAutomationById {
  automation: Automation;
  automationId: number;
}

export interface ToggleEnabledStateForAutomationByIdPayload {
  automationId: number;
  state: boolean;
}

export interface CreateAutomationForSourceActionPayload {
  source: AutomationSource;
  sourceId: number;
  automation: Automation;
}

export const updateAllAutomationAction =
  createAction<UpdateAllAutomationsPayload>(
    '@@TEMPLATES/AUTOMATIONS/updateAutomations/all',
  );

export const updateAutomationByIdAction = createAction<UpdateAutomationById>(
  '@@TEMPLATES/AUTOMATIONS/updateAutomations/byId',
);

export const createAutomationForSourceAction =
  createAction<CreateAutomationForSourceActionPayload>(
    '@@TEMPLATES/AUTOMATIONS/createAutomation/forSource',
  );

export const toggleEnabledStateForAutomationByIdAction =
  createAction<ToggleEnabledStateForAutomationByIdPayload>(
    '@@TEMPLATES/AUTOMATIONS/updateAutomations/toggleEnabled',
  );
